import * as React from 'react';
import UISelect from 'UIComponents/input/UISelect';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DayOfWeekArray } from 'automation-ui-canvas/constants/DayOfWeek';
import { DayOfWeekPresets } from 'automation-ui-canvas/flow/constants/DayOfWeekPresets';
import { TimePicker } from 'foundations-components/transitional/components/TimePicker';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { DeleteIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledFormattedMessage = props => {
  return /*#__PURE__*/_jsx(View, {
    marginInlineEnd: "200",
    children: /*#__PURE__*/_jsx(FormattedMessage, Object.assign({}, props))
  });
};
const DAYS_OF_WEEK_LANG_KEY = 'automation.common.daysOfWeek';
const MIN_SELECT_WIDTH = 180;
const TimeRangePicker = ({
  index,
  onUpdateStagedTimeWindowMetadata,
  stagedTimeWindowMetadata,
  readOnly,
  use = 'default'
}) => {
  const handleUpdateDay = function handleUpdateDay({
    target: {
      value
    }
  }) {
    const updatedTimeWindowMetadata = stagedTimeWindowMetadata.updateTimeRangeDay(index, value);
    onUpdateStagedTimeWindowMetadata(updatedTimeWindowMetadata);
  };
  const handleUpdateStartTime = function handleUpdateStartTime({
    target: {
      value
    }
  }) {
    const updatedTimeWindowMetadata = stagedTimeWindowMetadata.updateTimeRangeStartTime(index, value);
    onUpdateStagedTimeWindowMetadata(updatedTimeWindowMetadata);
  };
  const handleUpdateEndTime = function handleUpdateEndTime({
    target: {
      value
    }
  }) {
    const updatedTimeWindowMetadata = stagedTimeWindowMetadata.updateTimeRangeEndTime(index, value);
    onUpdateStagedTimeWindowMetadata(updatedTimeWindowMetadata);
  };
  const handleRemoveTimeRange = function handleRemoveTimeRange() {
    const updatedTimeWindowMetadata = stagedTimeWindowMetadata.removeTimeRange(index);
    onUpdateStagedTimeWindowMetadata(updatedTimeWindowMetadata);
  };
  const dayOptions = Object.values(DayOfWeekPresets).concat(DayOfWeekArray).map(dayEnum => ({
    text: I18n.text(`${DAYS_OF_WEEK_LANG_KEY}.${dayEnum}`),
    value: dayEnum
  }));
  const selectedDay = stagedTimeWindowMetadata.getDay(index);
  const selectedStartTime = stagedTimeWindowMetadata.getStartTimeInMinutes(index);
  const selectedEndTime = stagedTimeWindowMetadata.getEndTimeInMinutes(index);
  const day = /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": `platform-settings-timing-day-${index}`,
    options: dayOptions,
    onChange: handleUpdateDay,
    value: selectedDay,
    buttonUse: use === 'compact' ? 'form-on-dark' : undefined,
    readOnly: readOnly,
    clearable: false
  });
  const startTime = /*#__PURE__*/_jsx(TimePicker, {
    "data-test-id": `platform-settings-timing-time-picker-start-${index}`,
    value: selectedStartTime,
    onChange: handleUpdateStartTime,
    max: selectedEndTime - (selectedEndTime === 1439 ? 29 : 30),
    readOnly: readOnly,
    use: use === 'compact' ? 'on-dark' : undefined,
    clearable: false
  });
  const endTime = /*#__PURE__*/_jsx(TimePicker, {
    "data-test-id": `platform-settings-timing-time-picker-end-${index}`,
    value: selectedEndTime,
    onChange: handleUpdateEndTime,
    min: selectedStartTime + 30,
    extraTimes: [1439] // Adds 11:59PM as extra selection choice
    ,
    readOnly: readOnly,
    use: use === 'compact' ? 'on-dark' : undefined,
    clearable: false
  });
  const deleteBtn = /*#__PURE__*/_jsx(IconButton, {
    use: use === 'compact' ? 'link-on-bright' : 'link',
    onClick: handleRemoveTimeRange,
    disabled: readOnly,
    tooltip: readOnly && /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.readOnlyTooltip"
    }),
    tooltipProps: {
      placement: 'right'
    },
    children: /*#__PURE__*/_jsx(DeleteIcon, {})
  });
  if (use === 'default') {
    return /*#__PURE__*/_jsxs(Flex, {
      flexWrap: "nowrap",
      alignItems: "center",
      justifyContent: "start",
      children: [/*#__PURE__*/_jsx(View, {
        marginInlineEnd: "300",
        inlineSize: `${MIN_SELECT_WIDTH}px`,
        children: day
      }), /*#__PURE__*/_jsx(StyledFormattedMessage, {
        message: "platformSettings.timing.from"
      }), /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "300",
        inlineSize: `${MIN_SELECT_WIDTH}px`,
        children: startTime
      }), /*#__PURE__*/_jsx(StyledFormattedMessage, {
        message: "platformSettings.timing.to"
      }), /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "300",
        inlineSize: `${MIN_SELECT_WIDTH}px`,
        children: endTime
      }), deleteBtn]
    });
  } else {
    return /*#__PURE__*/_jsx(Tile, {
      compact: true,
      children: /*#__PURE__*/_jsx(View, {
        backgroundColor: "fill-surface-raised",
        children: /*#__PURE__*/_jsx(TileSection, {
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "start",
            children: [/*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              justifyContent: "start",
              children: [day, /*#__PURE__*/_jsxs(Flex, {
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "start",
                marginBlockStart: "200",
                columnGap: "300",
                children: [/*#__PURE__*/_jsxs("div", {
                  children: [/*#__PURE__*/_jsx(StyledFormattedMessage, {
                    message: "platformSettings.timing.from"
                  }), startTime]
                }), /*#__PURE__*/_jsxs("div", {
                  children: [/*#__PURE__*/_jsx(StyledFormattedMessage, {
                    message: "platformSettings.timing.to"
                  }), endTime]
                })]
              })]
            }), /*#__PURE__*/_jsx(Flex, {
              padding: "200",
              alignItems: "center",
              justifyContent: "center",
              children: deleteBtn
            })]
          })
        })
      })
    });
  }
};
export default TimeRangePicker;